// The daily routine (docs/DAILY-ROUTINE.md): a day of five exercises worked out on Friday and kept, read from
// /api/routine/{day}. Pure: which part is next, what state each one is in, where each one is played and what is
// said about the opening. The page (components/routine/) only paints this.

import type { DrillPosition } from "./positions.ts";
import { setUp } from "./positions.ts";
import { fenAfterUci } from "./linePosition.ts";
import type { LevelArea } from "./levelMetrics.ts";
import type { WelcomeStatus } from "./welcome.ts";
import type { AnalysisStatus } from "./firstAnalysis.ts";
import { isRunning, jobPercent } from "./diagnosisView.ts";

export type RoutineKind = "opening" | "find_opening" | "find_middlegame" | "convert";

/** What the server keeps of the opening: its name and how the last games through it went. */
export interface OpeningPayload {
    name?: string;
    family?: string;
    games?: number;
    points?: number;
    wins?: number;
    draws?: number;
    losses?: number;
    since?: string | null;
    /** Which end of their openings it came from, and its place there. */
    end?: "worst" | "best";
    rank?: number;
}

/** What the server keeps of a moment, for the screen and the email to say which game it was. */
export interface MomentPayload {
    phase?: string;
    color?: string;
    opponent?: string | null;
    opponentElo?: number | null;
    playedAt?: string | null;
    speed?: string | null;
    opening?: string | null;
}

export interface RoutineItem {
    slot: number;
    kind: RoutineKind;
    color: string | null;
    lineKey: string | null;
    gameId: number | null;
    ply: number | null;
    exercise: string | null;
    payload: OpeningPayload & MomentPayload;
    startedAt: string | null;
    doneAt: string | null;
    result: string | null;
    plays: number;
    /** The moment as Your positions plays it; null when it is no longer one of theirs (a deleted game). */
    position: DrillPosition | null;
}

export interface RoutineDay {
    day: string;
    today: boolean;
    weekStart: string;
    openedAt: string | null;
    startedAt: string | null;
    completedAt: string | null;
    missing: { kind: RoutineKind; reason: string }[] | null;
    rating: number | null;
    comment: string | null;
    lichessConnected: boolean;
    openingPlays: number;
    /** The fact of the day, already in the reader's language (routine/RoutineFacts); null when there is none. */
    fact: string | null;
    items: RoutineItem[];
}

/** A day with nothing in it, and why. */
export interface EmptyDay {
    day: string;
    today: boolean;
    reason: "no_games" | "nothing";
}

export function isEmptyDay(answer: RoutineDay | EmptyDay): answer is EmptyDay {
    return !("items" in answer);
}

/**
 * Where a part stands. `unavailable`: a moment of a game since deleted, which cannot be played and does not hold the
 * day back. The opening without Lichess is not unavailable: it is seen instead of played (`openingMode`).
 */
export type ItemState = "todo" | "doing" | "done" | "unavailable";

export function itemState(item: RoutineItem, _day?: Pick<RoutineDay, "lichessConnected">): ItemState {
    if (item.doneAt) return "done";
    if (item.kind !== "opening" && !item.position) return "unavailable";
    return item.startedAt || item.plays > 0 ? "doing" : "todo";
}

/**
 * How the opening is done. It is played against the moves real players made, which come from the Lichess explorer
 * and need the user's Lichess account (not to play there: an account and their permission). Without one it is
 * seen — the position and its moves — and the day goes on (decided 2026-11-01): an engine would be a different
 * opponent from the one this exercise is about.
 */
export function openingMode(day: Pick<RoutineDay, "lichessConnected">): "play" | "see" {
    return day.lichessConnected ? "play" : "see";
}

/** Where connecting Lichess sends the user back to: the day they were on. */
export function lichessConnectHref(day: string): string {
    return `/api/lichess/connect?returnTo=${encodeURIComponent(`/day/${day}`)}`;
}

/** The part to play now: the first one not done that can be played. None when the day is over. */
export function nextItem(day: RoutineDay): RoutineItem | null {
    return day.items.find((item) => {
        const state = itemState(item, day);
        return state === "todo" || state === "doing";
    }) ?? null;
}

/** How far the day has come: parts done out of the parts that can be played. */
export function dayProgress(day: RoutineDay): { done: number; total: number } {
    const playable = day.items.filter((item) => itemState(item, day) !== "unavailable");
    return { done: playable.filter((item) => item.doneAt).length, total: playable.length };
}

/** Whether there is nothing left to play: what sends the page to the feedback. */
export function isDayOver(day: RoutineDay): boolean {
    return !!day.completedAt || nextItem(day) === null;
}

/** The opening's plays so far, out of the ones the day asks for; more is allowed and shown as it is. */
/**
 * The opening of the day that is this line, if it is: the same moves and the same colour. The study shows the routine's
 * bar for it however it was reached (2026-11-01: from /day only, three practices of the same line counted for nothing).
 */
export function openingOfLine(day: Pick<RoutineDay, "items">, line: { uci?: string | null; color?: string | null } | null)
    : RoutineItem | null {
    const moves = (line?.uci || "").trim().split(/\s+/).join(" ");
    if (!moves) return null;
    return day.items.find((item) => item.kind === "opening" && item.color === line?.color
        && (item.lineKey || "").trim().split(/\s+/).join(" ") === moves) ?? null;
}

export function openingPlays(item: RoutineItem, day: Pick<RoutineDay, "openingPlays">): { played: number; needed: number; canContinue: boolean } {
    return { played: item.plays, needed: day.openingPlays, canContinue: item.plays >= day.openingPlays };
}

/** The exercise of Your positions a part is played as. */
export function positionsExercise(kind: RoutineKind): "opening" | "find" | "convert" | null {
    return kind === "find_opening" ? "opening" : kind === "find_middlegame" ? "find" : kind === "convert" ? "convert" : null;
}

/** The address of a moment of the day, on the page of Your positions in its routine mode. */
export function momentHref(day: string, slot: number): string {
    return `/tools/positions?routine=${encodeURIComponent(day)}&slot=${slot}`;
}

/** The address of the opening of the day, once there is a line to study. */
export function openingHref(day: string, slot: number, lineId: number): string {
    return `/tools/study?userLine=${lineId}&routine=${encodeURIComponent(day)}&slot=${slot}`;
}

/** The address of the day itself. Today is "/day"; any other day keeps its date, so it can be sent. */
export function dayHref(day: string, today: boolean): string {
    return today ? "/day" : `/day/${day}`;
}

/** What a page in routine mode reads from its address: which day and which part. */
export function routineParams(search: string): { day: string; slot: number } | null {
    const params = new URLSearchParams(search);
    const day = params.get("routine");
    const slot = Number(params.get("slot"));
    if (!day || !/^(today|\d{4}-\d{2}-\d{2})$/.test(day) || !Number.isInteger(slot) || slot < 1 || slot > 9) {
        return null;
    }
    return { day, slot };
}

/** The opening's points out of ten over its last games, to a tenth: 2.5, 0, 6. */
export function openingOutOfTen(payload: OpeningPayload): number | null {
    if (!payload.games) return null;
    return Math.round(((payload.points ?? 0) * 10) / payload.games * 10) / 10;
}

/** A part of a whole as a share, for the admin's tables: "40%", and "—" when there is no whole to be a part of. */
export function share(part: number, whole: number): string {
    return whole > 0 ? `${Math.round((part / whole) * 100)}%` : "—";
}

/** What the day came to, for the feedback: the moments found at the first try out of those answered, and the plays. */
export function daySummary(day: RoutineDay): { found: number; answered: number; openingPlays: number | null } {
    const moments = day.items.filter((item) => item.kind !== "opening" && item.doneAt);
    const opening = day.items.find((item) => item.kind === "opening");
    return {
        found: moments.filter((item) => item.result === "solved").length,
        answered: moments.length,
        openingPlays: opening && opening.plays > 0 ? opening.plays : null,
    };
}

/**
 * The board a part of the day shows beside its name, from the user's side: the opening where the line ends, a moment
 * where it is to be played (the position before the move of the game). None when there is nothing to draw: a line
 * without moves, or a moment whose game is gone.
 */
export function stepBoard(item: RoutineItem): { fen: string; orientation: string } | null {
    if (item.kind === "opening") {
        return item.lineKey ? { fen: fenAfterUci(item.lineKey), orientation: item.color || "white" } : null;
    }
    if (!item.position) return null;
    const at = setUp(item.position);
    return at ? { fen: at.fen, orientation: item.position.color } : null;
}

/**
 * Which parts of a day train an area of the level (docs/REFERENCE-POPULATION.md): the home says "what costs you most is
 * keeping an advantage, and today you train it twice". Precision is every move, so no part is about it alone.
 */
export const AREA_KINDS: Record<LevelArea, RoutineKind[]> = {
    precision: [],
    blunders: ["find_opening", "find_middlegame"],
    opening: ["opening", "find_opening"],
    middlegame: ["find_middlegame"],
    advantage: ["convert"],
};

export function timesTrained(area: LevelArea, day: RoutineDay): number {
    return day.items.filter((item) => AREA_KINDS[area].includes(item.kind)).length;
}

/** What /api/routine/recent answers: today in the reader's clock, and the days a routine was started. */
export interface RecentDays {
    today: string;
    trained: string[];
}

export interface WeekDay {
    day: string;
    /** 0 is Monday. */
    weekday: number;
    trained: boolean;
    today: boolean;
}

function shiftDay(day: string, by: number): string {
    const [y, m, d] = day.split("-").map(Number);
    return new Date(Date.UTC(y!, m! - 1, d! + by)).toISOString().slice(0, 10);
}

function weekdayOf(day: string): number {
    const [y, m, d] = day.split("-").map(Number);
    return (new Date(Date.UTC(y!, m! - 1, d!)).getUTCDay() + 6) % 7;
}

/** The last seven days, today last: the home's "your week" (decided 2026-11-01). */
export function lastSevenDays(recent: RecentDays): WeekDay[] {
    const trained = new Set(recent.trained);
    return Array.from({ length: 7 }, (_, i) => {
        const day = shiftDay(recent.today, i - 6);
        return { day, weekday: weekdayOf(day), trained: trained.has(day), today: i === 6 };
    });
}

/**
 * Days trained in a row up to today. Today not trained yet does not break it — the day is not over — so the run is
 * counted from yesterday then.
 */
export function runOfDays(recent: RecentDays): number {
    const trained = new Set(recent.trained);
    let day = trained.has(recent.today) ? recent.today : shiftDay(recent.today, -1);
    let run = 0;
    while (trained.has(day)) {
        run++;
        day = shiftDay(day, -1);
    }
    return run;
}

export type WayState = "done" | "now" | "next";

export interface WayStep {
    step: "where" | "games" | "analysis" | "first";
    state: WayState;
    /** The games in, for "games"; how far the analysis is (0-100), for "analysis" while it runs. */
    games?: number;
    percent?: number | null;
}

/**
 * What an account without a day yet is missing for its first one (decided 2026-11-01): somewhere to play connected,
 * its games in (the first import over), their analysis, and then the day. The home shows it in place of the day.
 */
export function stepsToFirstDay(welcome: WelcomeStatus | null, analysis: AnalysisStatus | null): WayStep[] {
    const connected = !!welcome?.anyProviderConnected;
    const games = welcome?.gamesImported ?? 0;
    const gamesIn = games > 0 && !welcome?.backfillRunning;
    const job = analysis?.job ?? null;
    const analysed = job?.status === "done";
    const running = isRunning(job);
    return [
        { step: "where", state: connected ? "done" : "now" },
        { step: "games", state: !connected ? "next" : gamesIn ? "done" : "now", games },
        {
            step: "analysis",
            state: analysed ? "done" : running || gamesIn ? "now" : "next",
            percent: running && job ? jobPercent(job) : null,
        },
        { step: "first", state: "next" },
    ];
}
